import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { CARD_CLASS } from "../styles";
import type { DynamicsData } from "../types";

type Props = {
  title: string;
  data: DynamicsData;
  yLabel: string;
};

const CLIENT_COLOR = "#fbbf24";
const COMPETITOR_PALETTE = ["#4f46e5", "#06b6d4", "#22c55e", "#f97316", "#a855f7", "#ef4444", "#0ea5e9", "#14b8a6", "#eab308", "#ec4899"];

const mixChannel = (channel: number, target: number, amount: number) =>
  Math.round(channel + (target - channel) * amount);

const withShade = (hex: string, shadeStep: number) => {
  if (shadeStep <= 0) return hex;
  const normalized = hex.replace("#", "");
  const channels = normalized.match(/.{1,2}/g);
  if (!channels || channels.length !== 3) return hex;

  const amount = Math.min(0.18 * shadeStep, 0.54);
  const target = shadeStep % 2 === 1 ? 255 : 0;
  const [r, g, b] = channels.map((channel) => parseInt(channel, 16));

  return `#${[r, g, b]
    .map((channel) => mixChannel(channel, target, amount).toString(16).padStart(2, "0"))
    .join("")}`;
};

const getCompetitorColor = (index: number) => {
  const baseColor = COMPETITOR_PALETTE[index % COMPETITOR_PALETTE.length];
  const shadeStep = Math.floor(index / COMPETITOR_PALETTE.length);
  return withShade(baseColor, shadeStep);
};

export default function DynamicsChart({ title, data, yLabel }: Props) {
  const hasData = data.dates.length > 0 && data.rows.length > 0;
  const chartKey = useMemo(
    () =>
      JSON.stringify({
        dates: data.dates,
        rows: data.rows.map((row) => ({ name: row.name, isClient: row.isClient, values: row.values })),
      }),
    [data],
  );

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "line" },
        valueFormatter: (value: number | null | undefined) =>
          typeof value === "number" ? value.toLocaleString("ru-RU") : "—",
        backgroundColor: "rgba(15,23,42,0.92)",
        borderColor: "#1e293b",
        textStyle: { color: "#f8fafc", fontSize: 12 },
      },
      legend: {
        top: 8,
        itemWidth: 14,
        itemHeight: 14,
        icon: "circle",
        textStyle: { color: "#64748b", fontSize: 12 },
      },
      grid: { left: 16, right: 24, top: 52, bottom: 36, containLabel: true },
      xAxis: {
        type: "category",
        data: data.dates,
        boundaryGap: false,
        name: "Дата сбора данных",
        nameLocation: "middle",
        nameGap: 26,
        nameTextStyle: { color: "#94a3b8", fontSize: 12 },
        axisLine: { lineStyle: { color: "#e2e8f0" } },
        axisLabel: { color: "#94a3b8" },
        axisTick: { show: false },
      },
      yAxis: {
        type: "value",
        name: yLabel,
        nameLocation: "middle",
        nameGap: 40,
        nameTextStyle: { color: "#94a3b8", fontSize: 12 },
        axisLine: { show: false },
        axisLabel: { color: "#94a3b8" },
        splitLine: { lineStyle: { color: "#f1f5f9" } },
      },
      series: (() => {
        let competitorIndex = 0;
        return data.rows.map((row) => {
          const color = row.isClient
            ? CLIENT_COLOR
            : getCompetitorColor(competitorIndex++);
        return {
          name: row.name,
          type: "line",
          data: row.values,
          smooth: true,
          symbol: "circle",
          symbolSize: row.isClient ? 7 : 5,
          lineStyle: {
            width: row.isClient ? 3 : 2,
            color,
          },
          itemStyle: { color },
        };
        });
      })(),
    }),
    [data, yLabel],
  );

  return (
    <section className={CARD_CLASS}>
      <div className="flex items-center gap-3">
        <span className="h-6 w-1.5 rounded-full bg-emerald-500" />
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">Сравнение цен по объектам</p>
        </div>
      </div>

      <div className="mt-4">
        {hasData ? (
          <ReactECharts key={chartKey} option={option} notMerge={true} style={{ height: 360, width: "100%" }} />
        ) : (
          <div className="grid h-[360px] place-items-center rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
            Нет данных для построения графика
          </div>
        )}
      </div>
    </section>
  );
}
